import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Card, 
  message, 
  Select, 
  DatePicker,
  Row,
  Col,
  Modal,
  Form,
  InputNumber,
  Alert,
  Switch,
  Avatar,
  Radio,
} from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, InfoCircleOutlined, DollarOutlined, ShopOutlined, GlobalOutlined, EyeOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
  accentDim: 'rgba(34, 211, 238, 0.1)',
};

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }

  /* Make Ant Design form tooltip/info icons visible on dark background */
  .ant-form-item-label > label .anticon,
  .ant-form-item-tooltip .anticon {
    color: ${COLORS.accent} !important;
  }
  
  .ant-form-item-tooltip-icon {
    color: ${COLORS.accent} !important;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const FilterRow = styled(Row)`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.span`
  color: ${COLORS.textSecondary};
  margin-right: 8px;
  display: inline-block;
  min-width: 120px;
`;

const DescriptionCard = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  .ant-alert {
    background: ${COLORS.bgPrimary} !important;
    border: 1px solid ${COLORS.border} !important;
    
    .ant-alert-message {
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-alert-description {
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-alert-icon {
      color: ${COLORS.accent} !important;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
  }
  
  .ant-modal-header {
    background: ${COLORS.bgSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-modal-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-modal-close {
    color: ${COLORS.textSecondary} !important;
    
    &:hover {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-modal-body {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-form-item-label > label {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-form-item-label > label .anticon,
  .ant-form-item-tooltip .anticon,
  .ant-form-item-tooltip-icon {
    color: ${COLORS.accent} !important;
  }
  
  .ant-input,
  .ant-input-password,
  .ant-select-selector,
  .ant-input-number,
  .ant-input-number-input,
  .ant-select-selection-search-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    
    &:hover,
    &:focus {
      border-color: ${COLORS.accent} !important;
    }
  }
  
  .ant-input::placeholder,
  .ant-select-selection-placeholder,
  .ant-input-number-input::placeholder {
    color: #64748B !important;
  }
  
  .ant-input-number-handler-wrap {
    background: ${COLORS.bgSecondary} !important;
    border-left: 1px solid ${COLORS.border} !important;
  }
  
  .ant-input-number-handler {
    color: ${COLORS.textSecondary} !important;
    
    &:hover {
      color: ${COLORS.accent} !important;
    }
  }
  
  .ant-radio-wrapper {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-radio-inner {
    border-color: ${COLORS.border} !important;
    background: ${COLORS.bgPrimary} !important;
  }
  
  .ant-radio-checked .ant-radio-inner {
    border-color: ${COLORS.accent} !important;
    background: ${COLORS.accent} !important;
  }
  
  .ant-radio-input:focus + .ant-radio-inner {
    border-color: ${COLORS.accent} !important;
  }
  
  .ant-select-dropdown {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
  }
  
  .ant-select-item {
    color: ${COLORS.textPrimary} !important;
    background: ${COLORS.bgSecondary} !important;
    
    &:hover {
      background: ${COLORS.hover} !important;
    }
    
    &.ant-select-item-option-selected {
      background: ${COLORS.accentDim} !important;
      color: ${COLORS.accent} !important;
    }
  }
`;

const SweepList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [sweepList, setSweepList] = useState<any[]>([]);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [chains, setChains] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [wallets, setWallets] = useState<any[]>([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [loadingChains, setLoadingChains] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [isValidated, setIsValidated] = useState(false);
  const [unknownTokenModalVisible, setUnknownTokenModalVisible] = useState(false);
  const [unknownTokenForm] = Form.useForm();
  const [submittingUnknownToken, setSubmittingUnknownToken] = useState(false);
  const [blockchainSelectMode, setBlockchainSelectMode] = useState<'select' | 'manual'>('select');
  const [currencySelectMode, setCurrencySelectMode] = useState<'select' | 'manual'>('select');

  // Filter states
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [merchantFilter, setMerchantFilter] = useState<string>(searchParams.get('merchantCompanyId') || '');
  const [walletFilter, setWalletFilter] = useState<string>(searchParams.get('walletId') || '');
  const [chainFilter, setChainFilter] = useState<string>(searchParams.get('chainId') || '');
  const [currencyFilter, setCurrencyFilter] = useState<string>(searchParams.get('currencyId') || '');
  const [sweepStatusFilter, setSweepStatusFilter] = useState<string>(searchParams.get('sweepStatus') || '');
  const [dateRange, setDateRange] = useState<any>(null);

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  // Load dropdown data
  useEffect(() => {
    fetchMerchants();
    fetchChains();
    fetchCurrencies();
  }, []);

  // Parse date range from URL params
  useEffect(() => {
    const dateFrom = searchParams.get('dateFrom');
    const dateTo = searchParams.get('dateTo');
    if (dateFrom && dateTo) {
      setDateRange([dayjs(dateFrom), dayjs(dateTo)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSweepList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, search, merchantFilter, walletFilter, chainFilter, currencyFilter, sweepStatusFilter, dateRange]);

  // Load wallets when merchant is selected
  useEffect(() => {
    if (merchantFilter) {
      fetchWallets(merchantFilter);
    } else {
      setWallets([]);
    }
  }, [merchantFilter]);

  // Check for walletId query param and auto-open create modal
  useEffect(() => {
    const walletIdParam = searchParams.get('walletId');
    if (walletIdParam) {
      // Open the create modal
      setCreateModalVisible(true);
      // Set the wallet ID in the form
      form.setFieldsValue({ walletId: walletIdParam });
      // Clear the query parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('walletId');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchMerchants = async () => {
    try {
      setLoadingMerchants(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_WALLET_MERCHANTS || '/api/Administrator/wallets/merchants');
      if (response.data?.status === 'success' && response.data?.data) {
        setMerchants(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
    } finally {
      setLoadingMerchants(false);
    }
  };

  const fetchChains = async () => {
    try {
      setLoadingChains(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS || '/api/Administrator/blockchain-networks', {
        params: { pageId: 1, perPage: 100, status: 'ACTIVE' }
      });
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setChains(data.itemsList || data.items || (Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching chains:', error);
      message.error('Failed to load blockchain networks');
    } finally {
      setLoadingChains(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      setLoadingCurrencies(true);
      const response = await apiClient.get('/api/Administrator/currencies', {
        params: { pageId: 1, perPage: 100, status: 'ACTIVE' }
      });
      if (response.data?.status === 'success' && response.data?.data) {
        setCurrencies(response.data.data.itemsList || []);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
    } finally {
      setLoadingCurrencies(false);
    }
  };

  const fetchWallets = async (merchantId: string) => {
    try {
      setLoadingWallets(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_WALLETS || '/api/Administrator/wallets', {
        params: { pageId: 1, perPage: 100, merchantId, status: 'ACTIVE' }
      });
      if (response.data?.status === 'success' && response.data?.data) {
        setWallets(response.data.data.itemsList || []);
      }
    } catch (error) {
      console.error('Error fetching wallets:', error);
    } finally {
      setLoadingWallets(false);
    }
  };

  const fetchSweepList = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
        sort: 'DESC',
      };

      if (search) params.search = search;
      if (merchantFilter) params.merchantCompanyId = merchantFilter;
      if (walletFilter) params.walletId = walletFilter;
      if (chainFilter) params.chainId = chainFilter;
      if (currencyFilter) params.currencyId = currencyFilter;
      if (sweepStatusFilter) params.sweepStatus = sweepStatusFilter;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.dateFrom = dateRange[0].format('YYYY-MM-DD');
        params.dateTo = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_SWEEP_LIST || '/api/Administrator/sweep-list', { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setSweepList(data.itemsList || []);
        const paginator = data.paginator;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total: paginator.itemCount || 0,
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching sweep list:', error);
      message.error(error.response?.data?.message || 'Failed to fetch sweep list');
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    newParams.set('pageId', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key: string, value: string | null) => {
    updateURLParams({ [key]: value });
  };

  const handleDateRangeChange = (dates: any) => {
    setDateRange(dates);
    updateURLParams({
      dateFrom: dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
      dateTo: dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
    });
  };

  const handleClearFilters = () => {
    setSearch('');
    setMerchantFilter('');
    setWalletFilter('');
    setChainFilter('');
    setCurrencyFilter('');
    setSweepStatusFilter('');
    setDateRange(null);
    setSearchParams(new URLSearchParams({ pageId: '1', perPage: '10' }));
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const handleCreate = () => {
    form.resetFields();
    setEditingItem(null);
    setCreateModalVisible(true);
  };

  const handleUnknownTokenSubmit = async (values: any) => {
    try {
      setSubmittingUnknownToken(true);
      
      const payload: any = {
        walletId: values.walletId || undefined,
        walletAddress: values.walletAddress || undefined,
        currencySymbol: values.currencySymbol,
        contractAddress: values.contractAddress || 'native',
        amount: values.amount,
        transactionHash: values.transactionHash || undefined,
      };

      // Handle blockchain network
      if (blockchainSelectMode === 'select' && values.blockchainNetworkId) {
        payload.blockchainNetworkId = values.blockchainNetworkId;
      } else if (blockchainSelectMode === 'manual') {
        if (values.blockchainNetworkIdInput) {
          payload.blockchainNetworkIdInput = values.blockchainNetworkIdInput;
        }
        if (values.blockchainName) {
          payload.blockchainName = values.blockchainName;
        }
        if (values.blockchainType) {
          payload.blockchainType = values.blockchainType;
        }
      }

      // Handle currency
      if (currencySelectMode === 'select' && values.currencyId) {
        payload.currencyId = values.currencyId;
      }

      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_CREATE_UNKNOWN_TOKEN_SWEEP_REQUEST,
        payload
      );

      if (response.data?.status === 'success') {
        message.success('Unknown token sweep request created successfully. Awaiting approval from another admin.');
        setUnknownTokenModalVisible(false);
        unknownTokenForm.resetFields();
        setBlockchainSelectMode('select');
        setCurrencySelectMode('select');
      } else {
        message.error(response.data?.message || 'Failed to create unknown token sweep request');
      }
    } catch (error: any) {
      console.error('Error creating unknown token sweep request:', error);
      message.error(error.response?.data?.message || 'Failed to create unknown token sweep request');
    } finally {
      setSubmittingUnknownToken(false);
    }
  };

  const handleValidate = async () => {
    try {
      const values = await form.validateFields(['walletId', 'walletAddress', 'chainId', 'currencyId', 'transactionHash', 'amount']);
      
      if (!values.walletId && !values.walletAddress) {
        message.error('Please enter a wallet ID or wallet address');
        return;
      }

      if (!values.chainId) {
        message.error('Please select a blockchain network');
        return;
      }

      setValidating(true);
      const payload = {
        walletId: values.walletId || undefined,
        walletAddress: values.walletAddress || undefined,
        chainId: values.chainId,
        currencyId: values.currencyId,
        transactionHash: values.transactionHash || undefined,
        amount: values.amount,
        merchantCompanyId: values.merchantCompanyId,
      };

      const response = await apiClient.post(API_ENDPOINTS.ADMIN_VALIDATE_SWEEP_LIST_ITEM || '/api/Administrator/sweep-list/validate', payload);
      
      if (response.data?.status === 'success') {
        const validation = response.data.data;
        setValidationResult(validation);
        setIsValidated(validation.valid);
        
        // If transactionHash is not provided, disable webhook notification
        if (!values.transactionHash && values.notifyMerchant) {
          form.setFieldsValue({ notifyMerchant: false });
          message.warning('Webhook notification has been disabled because transaction hash is not provided.');
        }
        
        // Auto-fill form fields from validation data if available
        if (validation.data?.transactionDetails) {
          const txDetails = validation.data.transactionDetails;
          form.setFieldsValue({
            amount: txDetails.amount,
            currencyId: txDetails.currencyId,
            currencySymbol: txDetails.currencySymbol,
          });
        }
        
        if (validation.valid) {
          message.success('Validation passed! You can now create the sweep list item.');
        } else {
          message.warning('Validation failed. Please fix the errors before creating.');
        }
      }
    } catch (error: any) {
      if (error.response?.data?.data) {
        setValidationResult(error.response.data.data);
        setIsValidated(false);
      } else {
        message.error(error.response?.data?.message || 'Validation failed');
      }
    } finally {
      setValidating(false);
    }
  };

  const handleCreateSubmit = async (values: any) => {
    try {
      if (!isValidated) {
        message.error('Please validate the form before creating');
        return;
      }

      if (!values.walletId && !values.walletAddress) {
        message.error('Please enter a wallet ID or wallet address');
        return;
      }

      // Only send required fields - backend will derive the rest
      const payload: any = {
        chainId: values.chainId,
        currencyId: values.currencyId,
        logNotionalBalance: values.logNotionalBalance !== false,
        notifyMerchant: values.transactionHash ? !!values.notifyMerchant : false, // Disable if no transactionHash
      };
      
      // Only include optional fields if they have values (don't include undefined)
      if (values.walletId) {
        payload.walletId = values.walletId;
      }
      if (values.walletAddress) {
        payload.walletAddress = values.walletAddress;
      }
      if (values.transactionHash) {
        payload.transactionHash = values.transactionHash;
      }
      if (values.transactionBlock) {
        payload.transactionBlock = values.transactionBlock;
      }
      await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_SWEEP_LIST_ITEM || '/api/Administrator/sweep-list/create', payload);
      message.success('Sweep list item created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      setValidationResult(null);
      setIsValidated(false);
      fetchSweepList();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create sweep list item');
    }
  };

  const handleEditSubmit = async (values: any) => {
    try {
      // Only send editable fields: status
      const payload: any = {
        sweepStatus: values.sweepStatus,
      };
      
      await apiClient.put(
        (API_ENDPOINTS.ADMIN_UPDATE_SWEEP_LIST_ITEM || '/api/Administrator/sweep-list/{id}/update').replace('{id}', editingItem.id),
        payload
      );
      message.success('Sweep list item updated successfully');
      setEditModalVisible(false);
      setEditingItem(null);
      form.resetFields();
      fetchSweepList();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update sweep list item');
    }
  };

  const columns = [
    {
      title: 'Wallet ID',
      dataIndex: ['wallet', 'walletId'],
      key: 'walletId',
      render: (id: string, record: any) => {
        if (!id) return '-';
        
        const walletUuid = record.wallet?.id;
        const displayId = record.wallet?.walletId || id;
        
        return (
          <span 
            style={{ 
              fontFamily: 'monospace', 
              fontSize: '12px',
              cursor: walletUuid ? 'pointer' : 'default',
              color: walletUuid ? COLORS.accent : COLORS.textPrimary,
            }}
            onClick={() => {
              if (walletUuid) {
                navigate(`/kivu/wallets/${walletUuid}`);
              }
            }}
          >
            {displayId.substring(0, 16)}...
          </span>
        );
      },
    },
    {
      title: 'Wallet Address',
      dataIndex: ['wallet', 'evmAddress'],
      key: 'walletAddress',
      render: (_: any, record: any) => {
        const chainType = (record.blockchainNetwork?.blockchainType || 'evm').toLowerCase();
        let address = '-';
        
        if (chainType === 'bitcoin' && record.wallet?.bitcoinAddress) {
          address = record.wallet.bitcoinAddress;
        } else if (chainType === 'tron' && record.wallet?.tronAddress) {
          address = record.wallet.tronAddress;
        } else if (chainType === 'solana' && record.wallet?.solanaAddress) {
          address = record.wallet.solanaAddress;
        } else if (record.wallet?.evmAddress) {
          address = record.wallet.evmAddress;
        }
        
        return (
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {address === '-' ? '-' : `${address.substring(0, 10)}...${address.substring(address.length - 6)}`}
          </span>
        );
      },
    },
    {
      title: 'Merchant',
      dataIndex: ['merchantCompany', 'name'],
      key: 'merchant',
      render: (name: string, record: any) => (
        <Space
          style={{ cursor: record.merchantCompany?.id ? 'pointer' : 'default' }}
          onClick={() => record.merchantCompany?.id && navigate(`/kivu/companies/${record.merchantCompany.id}`)}
        >
          <Avatar
            src={record.merchantCompany?.logoUrl}
            icon={<ShopOutlined />}
            size={24}
            style={{ 
              backgroundColor: COLORS.bgSecondary, 
              color: COLORS.textPrimary, 
              border: `1px solid ${COLORS.border}` 
            }}
          />
          <span style={{ color: record.merchantCompany?.id ? COLORS.accent : undefined }}>{name || '-'}</span>
        </Space>
      ),
    },
    {
      title: 'Chain',
      dataIndex: ['blockchainNetwork', 'name'],
      key: 'chain',
      render: (name: string, record: any) => (
        <Space>
          <Avatar
            src={record.blockchainNetwork?.iconUrl}
            icon={<GlobalOutlined />}
            size={20}
            style={{ 
              backgroundColor: COLORS.bgSecondary, 
              color: COLORS.textPrimary, 
              border: `1px solid ${COLORS.border}` 
            }}
          />
          <span>{name || '-'}</span>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'sweepStatus',
      key: 'sweepStatus',
      render: (status: string) => (
        <Tag color={status === 'COMPLETED' ? 'green' : status === 'FAILED' ? 'red' : 'orange'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Currency',
      dataIndex: ['currency', 'symbol'],
      key: 'currency',
      render: (symbol: string, record: any) => (
        <Space>
          <Avatar
            src={record.currency?.iconUrl}
            icon={<DollarOutlined />}
            size={20}
            style={{ 
              backgroundColor: COLORS.bgSecondary, 
              color: COLORS.textPrimary, 
              border: `1px solid ${COLORS.border}` 
            }}
          />
          <span>{symbol || '-'}</span>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <span>
          {Number(amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 8,
          })} {record.currencySymbol}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/kivu/sweep-list/${record.id}`)}
          style={{ color: COLORS.accent }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Sweep List
        </Title>
        <Space>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={handleCreate}
          >
            Create Sweep List Item
          </Button>
          <Button
            icon={<DollarOutlined />}
            onClick={() => {
              unknownTokenForm.resetFields();
              setBlockchainSelectMode('select');
              setCurrencySelectMode('select');
              setUnknownTokenModalVisible(true);
            }}
          >
            Retrieve Unknown Token
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchSweepList}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </HeaderContainer>

      <DescriptionCard>
        <Alert
          message="About Sweep List (Remittance)"
          description={
            <div>
              <Text style={{ color: COLORS.textPrimary }}>
                The Sweep List (also referred to as <strong>Remittance</strong>) is a list of funding transactions that need to be swept to merchant custodial wallets. 
                When a transaction is received from OnchainWatcher, it will send a webhook to the admin dashboard to create/add the transaction to the sweep list. 
                This feature allows administrators to manage and track all pending and completed sweep transactions.
              </Text>
            </div>
          }
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{
            background: COLORS.bgPrimary,
            border: `1px solid ${COLORS.border}`,
          }}
        />
      </DescriptionCard>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <FilterRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Search</FilterLabel>
              <Input
                placeholder="Wallet ID, transaction hash, currency"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={() => handleFilterChange('search', search || null)}
                onBlur={() => handleFilterChange('search', search || null)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Merchant</FilterLabel>
              <Select
                placeholder="All merchants"
                value={merchantFilter || undefined}
                onChange={(value) => {
                  setMerchantFilter(value);
                  handleFilterChange('merchantCompanyId', value || null);
                }}
                allowClear
                showSearch
                loading={loadingMerchants}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                options={merchants.map(m => ({ value: m.id, label: m.name }))}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Wallet</FilterLabel>
              <Select
                placeholder="All wallets"
                value={walletFilter || undefined}
                onChange={(value) => {
                  setWalletFilter(value);
                  handleFilterChange('walletId', value || null);
                }}
                allowClear
                showSearch
                loading={loadingWallets}
                disabled={!merchantFilter}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                options={wallets.map(w => ({ value: w.walletId, label: w.walletId }))}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Chain</FilterLabel>
              <Select
                placeholder="All chains"
                value={chainFilter || undefined}
                onChange={(value) => {
                  setChainFilter(value);
                  handleFilterChange('chainId', value || null);
                }}
                allowClear
                showSearch
                loading={loadingChains}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                options={chains.map(c => ({ 
                value: c.id, 
                label: c.networkId ? `${c.name} [${c.networkId}]` : c.name 
              }))}
              />
            </Col>
          </FilterRow>
          <FilterRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Currency</FilterLabel>
              <Select
                placeholder="All currencies"
                value={currencyFilter || undefined}
                onChange={(value) => {
                  setCurrencyFilter(value);
                  handleFilterChange('currencyId', value || null);
                }}
                allowClear
                showSearch
                loading={loadingCurrencies}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                options={currencies.map(c => ({ value: c.id, label: `${c.symbol} - ${c.name}` }))}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Sweep Status</FilterLabel>
              <Select
                placeholder="All statuses"
                value={sweepStatusFilter || undefined}
                onChange={(value) => {
                  setSweepStatusFilter(value);
                  handleFilterChange('sweepStatus', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="PENDING">Pending</Select.Option>
                <Select.Option value="COMPLETED">Completed</Select.Option>
                <Select.Option value="FAILED">Failed</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Date Range</FilterLabel>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Button onClick={handleClearFilters} style={{ marginTop: 24 }}>
                Clear Filters
              </Button>
            </Col>
          </FilterRow>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={sweepList}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} items
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            size="small"
          />
        </TableScrollWrapper>
      </Card>

      {/* Create Modal */}
      <StyledModal
        title="Create Sweep List Item (Request Instant Remittance of funds)"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
          setValidationResult(null);
          setIsValidated(false);
        }}
        onOk={() => form.submit()}
        width={700}
        okText="Create"
        cancelText="Cancel"
        okButtonProps={{ disabled: !isValidated }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Form.Item
            name="walletId"
            label="Wallet ID"
            tooltip="You can provide either a Wallet ID or a wallet address below."
          >
            <Input placeholder="Enter wallet ID" />
          </Form.Item>
          <Form.Item
            name="walletAddress"
            label="Wallet Address"
            tooltip="Optional alternative lookup by EVM / Bitcoin / Solana address if you don't have the Wallet ID."
          >
            <Input placeholder="Enter wallet address" />
          </Form.Item>
          <Form.Item
            name="chainId"
            label="Blockchain Network"
            rules={[{ required: true, message: 'Please select a blockchain network' }]}
          >
            <Select
              placeholder="Select blockchain network"
              showSearch
              loading={loadingChains}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={chains.map(c => ({ 
                value: c.id, 
                label: c.networkId ? `${c.name} [${c.networkId}]` : c.name 
              }))}
            />
          </Form.Item>
          <Form.Item
            name="currencyId"
            label="Currency"
            rules={[{ required: true, message: 'Please select a currency' }]}
          >
            <Select
              placeholder="Select currency"
              showSearch
              loading={loadingCurrencies}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={currencies.map(c => ({ value: c.id, label: `${c.symbol} - ${c.name}` }))}
            />
          </Form.Item>
          <Form.Item
            name="transactionHash"
            label="Transaction Hash (Optional)"
            tooltip="Transaction hash is optional. If not provided, the system will attempt to derive it from other information."
          >
            <Input placeholder="Enter transaction hash (0x...) - Optional" />
          </Form.Item>
          <Form.Item
            name="transactionBlock"
            label="Transaction Block (Optional)"
            tooltip="Block number will be automatically fetched from the transaction if not provided"
          >
            <InputNumber
              placeholder="Enter block number (optional)"
              style={{ width: '100%' }}
              min={0}
            />
          </Form.Item>
          <Form.Item
            name="logNotionalBalance"
            label="Log Notional Balance"
            initialValue={true}
            valuePropName="checked"
            tooltip="If enabled, the wallet's notional balance (mixed-currency running total) will be incremented when this sweep item is created."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="notifyMerchant"
            label="Notify Merchant via Webhook"
            initialValue={true}
            valuePropName="checked"
            tooltip="If enabled and the wallet belongs to a merchant, a webhook will be sent to the merchant with sweep details and the delivery status will be logged. This will be automatically disabled if transaction hash is not provided."
          >
            <Switch disabled={!form.getFieldValue('transactionHash')} />
          </Form.Item>
          
          {/* Validation Button */}
          <Form.Item>
            <Button
              type="primary"
              onClick={handleValidate}
              loading={validating}
              block
              style={{ marginBottom: 16 }}
            >
              Validate
            </Button>
          </Form.Item>

          {/* Validation Results */}
          {validationResult && (
            <div style={{ marginBottom: 16 }}>
              {validationResult.errors && validationResult.errors.length > 0 && (
                <Alert
                  message={<span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Validation Errors</span>}
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {validationResult.errors.map((err: any, idx: number) => (
                        <li key={idx} style={{ color: '#ff4d4f' }}>
                          <strong>{err.field}:</strong> {err.message}
                        </li>
                      ))}
                    </ul>
                  }
                  type="error"
                  showIcon
                  style={{ 
                    marginBottom: 8,
                    background: '#000000',
                    border: '1px solid #ff4d4f',
                    borderRadius: '4px'
                  }}
                />
              )}
              {validationResult.warnings && validationResult.warnings.length > 0 && (
                <Alert
                  message="Warnings"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {validationResult.warnings.map((warn: any, idx: number) => (
                        <li key={idx} style={{ color: COLORS.textPrimary }}>
                          <strong>{warn.field}:</strong> {warn.message}
                        </li>
                      ))}
                    </ul>
                  }
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                />
              )}
              {validationResult.valid && (
                <Alert
                  message="Validation Passed"
                  description="All validations passed. You can now create the sweep list item."
                  type="success"
                  showIcon
                />
              )}
              {validationResult.data && (
                <div style={{ marginTop: 12, padding: 12, background: COLORS.bgPrimary, borderRadius: 4 }}>
                  {validationResult.data.wallet && (
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: COLORS.accent }}>Wallet: </Text>
                      <Text style={{ color: COLORS.textPrimary }}>{validationResult.data.wallet.walletId}</Text>
                      {validationResult.data.wallet.address && (
                        <div style={{ marginTop: 4 }}>
                          <Text strong style={{ color: COLORS.accent, fontSize: '12px' }}>Address: </Text>
                          <Text style={{ color: COLORS.textPrimary, fontSize: '12px', fontFamily: 'monospace' }}>
                            {validationResult.data.wallet.address}
                          </Text>
                          {validationResult.data.wallet.explorerUrl && (
                            <a
                              href={validationResult.data.wallet.explorerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: COLORS.accent, 
                                marginLeft: 8,
                                fontSize: '12px',
                                textDecoration: 'underline'
                              }}
                            >
                              View on Explorer ↗
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {validationResult.data.balance && (
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: COLORS.accent }}>Balance: </Text>
                      <Text style={{ color: COLORS.textPrimary }}>
                        {validationResult.data.balance.currentBalance}
                        {validationResult.data.balance.minSweepAmount && validationResult.data.balance.minSweepAmount > 0 && ` (Min: ${validationResult.data.balance.minSweepAmount})`}
                      </Text>
                    </div>
                  )}
                  {validationResult.data.gasReport && (
                    <div>
                      <Text strong style={{ color: COLORS.accent }}>Gas: </Text>
                      <Text style={{ color: validationResult.data.gasReport.isLowGas ? '#ff4d4f' : COLORS.textPrimary }}>
                        {validationResult.data.gasReport.gasBalance} {validationResult.data.gasReport.currencySymbol} (${validationResult.data.gasReport.gasBalanceUsd.toFixed(2)})
                        {validationResult.data.gasReport.isLowGas && ' - Low Gas!'}
                      </Text>
                    </div>
                  )}
                  {validationResult.data.transactionDetails && (
                    <div style={{ marginTop: 8 }}>
                      {validationResult.data.transactionDetails.rpcUsed && (
                        <div style={{ marginBottom: 4 }}>
                          <Text strong style={{ color: COLORS.accent, fontSize: '12px' }}>RPC Used: </Text>
                          <Text style={{ color: COLORS.textPrimary, fontSize: '12px' }}>
                            {validationResult.data.transactionDetails.rpcUsed}
                          </Text>
                          {validationResult.data.transactionDetails.rpcUrl && (
                            <Text style={{ color: COLORS.textSecondary, fontSize: '11px', fontFamily: 'monospace', marginLeft: 8 }}>
                              ({validationResult.data.transactionDetails.rpcUrl})
                            </Text>
                          )}
                        </div>
                      )}
                      {validationResult.data.transactionDetails.failedRpcs && validationResult.data.transactionDetails.failedRpcs.length > 0 && (
                        <div>
                          <Text strong style={{ color: '#ff9800', fontSize: '12px' }}>Failed RPCs: </Text>
                          <Text style={{ color: COLORS.textSecondary, fontSize: '11px', fontFamily: 'monospace' }}>
                            {validationResult.data.transactionDetails.failedRpcs.join(', ')}
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <Alert
            message="Note"
            description="Please validate the form before creating. Merchant company, currency, and amount will be automatically extracted from the wallet and transaction hash if available."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </Form>
      </StyledModal>

      {/* Edit Modal */}
      <StyledModal
        title="Edit Sweep List Item"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingItem(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
        okText="Update"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          {/* Read-only fields for reference */}
          {editingItem && (
            <>
              <Form.Item label="Merchant Company">
                <Input value={editingItem.merchantCompany?.name || '-'} disabled />
              </Form.Item>
              <Form.Item label="Wallet ID">
                <Input value={editingItem.wallet?.walletId || '-'} disabled />
              </Form.Item>
              <Form.Item label="Blockchain Network">
                <Input value={editingItem.blockchainNetwork?.name || '-'} disabled />
              </Form.Item>
              <Form.Item label="Currency">
                <Input value={`${editingItem.currencySymbol} - ${editingItem.currency?.name || ''}`} disabled />
              </Form.Item>
              <Form.Item label="Amount">
                <InputNumber
                  value={Number(editingItem.amount)}
                  disabled
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </>
          )}
          
          {/* Editable fields - Only Status */}
          <Form.Item
            name="sweepStatus"
            label="Sweep Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select
              placeholder="Select status"
              options={[
                { value: 'PENDING', label: 'PENDING' },
                { value: 'COMPLETED', label: 'COMPLETED' },
                { value: 'FAILED', label: 'FAILED' },
              ]}
            />
          </Form.Item>
        </Form>
      </StyledModal>

      {/* Retrieve Unknown Token Modal */}
      <StyledModal
        title="Retrieve Unknown Token (Admin)"
        open={unknownTokenModalVisible}
        onCancel={() => {
          setUnknownTokenModalVisible(false);
          unknownTokenForm.resetFields();
          setBlockchainSelectMode('select');
          setCurrencySelectMode('select');
        }}
        onOk={() => unknownTokenForm.submit()}
        width={700}
        okText="Submit Request"
        cancelText="Cancel"
        okButtonProps={{ loading: submittingUnknownToken }}
      >
        <Alert
          message="Admin Approval Required"
          description="This request will be sent to other administrators for approval. The merchant company will be automatically determined from the wallet. Once approved by another admin, the blockchain network, currency, and contract will be created with PARTIAL status (for sweep purposes only), and a sweep request will be automatically created."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
        <Form
          form={unknownTokenForm}
          layout="vertical"
          onFinish={handleUnknownTokenSubmit}
        >
          <Form.Item
            name="walletId"
            label="Wallet ID"
            tooltip="You can either select a wallet here or provide a wallet address below. The merchant company will be automatically determined from the wallet."
          >
            <Select
              placeholder="Select wallet (optional)"
              showSearch
              loading={loadingWallets}
              allowClear
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={wallets.map(w => ({ value: w.walletId, label: w.walletId }))}
            />
          </Form.Item>
          <Form.Item
            name="walletAddress"
            label="Wallet Address"
            tooltip="Optional alternative lookup by EVM / Bitcoin / Solana address if you don't want to select a Wallet ID."
          >
            <Input placeholder="Enter wallet address (optional)" />
          </Form.Item>
          
          {/* Blockchain Network Selection */}
          <Form.Item label="Blockchain Network" required>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio.Group
                value={blockchainSelectMode}
                onChange={(e) => {
                  setBlockchainSelectMode(e.target.value);
                  unknownTokenForm.setFieldsValue({ blockchainNetworkId: undefined, blockchainName: undefined, blockchainNetworkIdInput: undefined, blockchainType: undefined });
                }}
              >
                <Radio value="select">Select Existing</Radio>
                <Radio value="manual">Enter Manually</Radio>
              </Radio.Group>
              {blockchainSelectMode === 'select' ? (
                <Form.Item
                  name="blockchainNetworkId"
                  rules={[{ required: true, message: 'Please select a blockchain network' }]}
                >
                  <Select
                    placeholder="Select blockchain network"
                    showSearch
                    loading={loadingChains}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={chains.map(c => ({ 
                      value: c.id, 
                      label: c.networkId ? `${c.name} [${c.networkId}]` : c.name 
                    }))}
                  />
                </Form.Item>
              ) : (
                <>
                  <Form.Item
                    name="blockchainName"
                    label="Blockchain Name"
                    rules={[{ required: true, message: 'Please enter blockchain name' }]}
                  >
                    <Input placeholder="e.g., Ethereum, Bitcoin, Solana" />
                  </Form.Item>
                  <Form.Item
                    name="blockchainNetworkIdInput"
                    label="Network ID (for EVM chains)"
                    tooltip="Numeric network ID for EVM chains (e.g., 1 for Ethereum mainnet, 137 for Polygon)"
                  >
                    <InputNumber
                      placeholder="Enter network ID"
                      style={{ width: '100%' }}
                      min={0}
                    />
                  </Form.Item>
                  <Form.Item
                    name="blockchainType"
                    label="Blockchain Type"
                    rules={[{ required: true, message: 'Please select blockchain type' }]}
                  >
                    <Select placeholder="Select blockchain type">
                      <Select.Option value="EVM">EVM</Select.Option>
                      <Select.Option value="BITCOIN">Bitcoin</Select.Option>
                      <Select.Option value="SOLANA">Solana</Select.Option>
                    </Select>
                  </Form.Item>
                </>
              )}
            </Space>
          </Form.Item>

          {/* Currency Selection */}
          <Form.Item label="Currency" required>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio.Group
                value={currencySelectMode}
                onChange={(e) => {
                  setCurrencySelectMode(e.target.value);
                  unknownTokenForm.setFieldsValue({ currencyId: undefined, currencySymbol: undefined });
                }}
              >
                <Radio value="select">Select Existing</Radio>
                <Radio value="manual">Enter Symbol</Radio>
              </Radio.Group>
              {currencySelectMode === 'select' ? (
                <Form.Item
                  name="currencyId"
                  rules={[{ required: true, message: 'Please select a currency' }]}
                >
                  <Select
                    placeholder="Select currency"
                    showSearch
                    loading={loadingCurrencies}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={currencies.map(c => ({ value: c.id, label: `${c.symbol} - ${c.name}` }))}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="currencySymbol"
                  rules={[{ required: true, message: 'Please enter currency symbol' }]}
                >
                  <Input placeholder="e.g., ETH, BTC, USDT" />
                </Form.Item>
              )}
            </Space>
          </Form.Item>

          <Form.Item
            name="contractAddress"
            label="Contract Address"
            tooltip='Enter the contract address, or "native" for native tokens (e.g., ETH, BTC, SOL)'
            rules={[{ required: true, message: 'Please enter contract address or "native"' }]}
          >
            <Input placeholder='Contract address or "native" for native tokens' />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: 'Please enter amount' }]}
          >
            <InputNumber
              placeholder="Enter amount"
              style={{ width: '100%' }}
              min={0}
              step={0.00000001}
              precision={18}
            />
          </Form.Item>

          <Form.Item
            name="transactionHash"
            label="Transaction Hash (Optional)"
            tooltip="Optional transaction hash if you have it"
          >
            <Input placeholder="Enter transaction hash (0x...)" />
          </Form.Item>
        </Form>
      </StyledModal>

    </PageContainer>
  );
};

export default SweepList;

